import React, { useCallback, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { BodyText, Icon, usePressableState } from '@sg/garnish';

/**
 * "Add utensils to my bag" checkbox in the bottom of the bag.
 */
export const BagUtensilsCta = (props: BagUtensilsCtaProps) => {
  const { shouldIncludeUtensils, isDisabled, onChange } = props;

  const ref = useRef(null);
  const { formatMessage } = useIntl();

  const { isInteracting } = usePressableState(ref);
  const interactiveStyle =
    isInteracting && !isDisabled ? styles.checkerHover : null;

  const iconStyle = getIconStyle(isDisabled, shouldIncludeUtensils);
  const iconColor = getIconColor(isDisabled, shouldIncludeUtensils);

  const handleOnChange = useCallback(() => {
    onChange({ shouldIncludeUtensils: !shouldIncludeUtensils });
  }, [shouldIncludeUtensils, onChange]);

  return (
    <Pressable
      ref={ref}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: shouldIncludeUtensils }}
      accessibilityLabel={formatMessage(messages.utensilsCta)}
      disabled={isDisabled}
      style={styles.container}
      onPress={handleOnChange}
    >
      <View style={[styles.content, interactiveStyle]}>
        <View>
          <BodyText sizeMatch={['16']}>
            <FormattedMessage {...messages.utensilsCta} />
          </BodyText>

          <BodyText sizeMatch={['14']}>
            <FormattedMessage {...messages.utensilsHint} />
          </BodyText>
        </View>

        <View style={[styles.checkerBase, iconStyle]}>
          <Icon
            name="IconCheck"
            width={CHECK_ICON_SIZE}
            height={CHECK_ICON_SIZE}
            color={iconColor}
          />
        </View>
      </View>
    </Pressable>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const getIconStyle = (isDisabled: boolean, shouldIncludeUtensils: boolean) => {
  if (isDisabled) return styles.checkerDisabled;
  if (shouldIncludeUtensils) return styles.checkerChecked;
  return styles.checkerUnchecked;
};

const getIconColor = (isDisabled: boolean, shouldIncludeUtensils: boolean) => {
  if (isDisabled) return theme.colors.OPACITY.DARK_KALE.DARK;
  if (shouldIncludeUtensils) return theme.colors.WHITE;
  return theme.colors.OPACITY.TRANSPARENT;
};

// ─── Constants ──────────────────────────────────────────────────────────────

const CHECK_ICON_SIZE = 24;
const CHECK_WRAPPER_SIZE = 32;

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  utensilsCta: {
    defaultMessage: 'Add utensils to my bag',
    description: 'Bag | Utensils | Cta (on)',
  },
  utensilsHint: {
    defaultMessage: 'Our utensils and napkins are 100% compostable',
    description: 'Bag | Utensils | Hint',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderBottomWidth: theme.spacing['2'],
    marginHorizontal: -theme.spacing['4'],
  },
  content: {
    margin: theme.spacing['2'],
    paddingHorizontal: theme.spacing['2'],
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
    borderRadius: theme.radius.medium,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  checkerBase: {
    width: CHECK_WRAPPER_SIZE,
    height: CHECK_WRAPPER_SIZE,
    borderRadius: theme.radius.xxxlarge,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkerChecked: {
    backgroundColor: theme.colors.SPINACH,
  },
  checkerUnchecked: {
    borderColor: theme.colors.SPINACH,
    borderWidth: 1,
    backgroundColor: theme.colors.OPACITY.TRANSPARENT,
  },
  checkerDisabled: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  checkerHover: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagUtensilsCtaProps = {
  shouldIncludeUtensils: boolean;
  isDisabled: boolean;
  onChange: (payload: { shouldIncludeUtensils: boolean }) => void;
};
